import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { config, GLOBAL_SCOPES, TEAM_BOUND_SCOPES, isFeatureHidden } from '../../app/utils/config';
import Langs from '../../app/lang/langs';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import { getManagersProfiles, getManagersPermissions, updateManagersPermissions } from "../../components/permissions/permissionsSlice";
import RolePermissionCard from './RolePermissionCard';
import pStyles from './Permissions.module.css';

export default function ManagersPermissionTab({ defaultProfile }) {

    let dispatch = useDispatch()

    let permissions = useSelector((state) => state.permissions.managersPermissions)
    let managersProfiles = useSelector((state) => state.permissions.managersProfiles)

    let roles = managersProfiles.map(p => p.name)

    useEffect(() => {
        dispatch(getManagersProfiles({ all: true }))
    }, [dispatch])

    // A hidden feature leaves the grid entirely: no row to grant, and — because these two
    // lists are also what buildPermissionsFromApi validates against — any grant already on
    // file for it is dropped from what this tab reads. That is deliberate. The row would
    // otherwise hand out rights to a page nobody can reach, and the grant itself is
    // untouched in the database, so un-hiding the feature brings it back exactly as it was.
    let globalPermissions = GLOBAL_SCOPES.filter(scope => !isFeatureHidden(scope))
    let teamPermissions = TEAM_BOUND_SCOPES.filter(scope => !isFeatureHidden(scope))

    let buildRoleDefaults = (rolesArr) => {
        return rolesArr.reduce((acc, role) => {
            acc[role] = {
                global: { view: [], edit: [], remove: [] },
                team: { view: [], edit: [], view_all: [], edit_all: [], remove: [] }
            }
            return acc
        }, {})
    }

    let buildPermissionsFromApi = () => {
        let newPermissions = buildRoleDefaults(roles)
        Object.keys(newPermissions).forEach(role => {
            if (!permissions[role]) {
                newPermissions[role] = {
                    global: {
                        [config.permissionTypes.view]: globalPermissions.filter(s => s === 'news'),
                        [config.permissionTypes.edit]: [],
                        [config.permissionTypes.remove]: globalPermissions.filter(s => s !== 'news')
                    },
                    team: {
                        [config.permissionTypes.view]: teamPermissions.filter(s => s !== 'evaluations' && s !== 'ventr' && s !== 'warnings'),
                        [config.permissionTypes.edit]: [],
                        [config.permissionTypes.view_all]: [],
                        [config.permissionTypes.edit_all]: [],
                        [config.permissionTypes.remove]: teamPermissions.filter(s => s === 'evaluations' || s === 'ventr' || s === 'warnings')
                    }
                }
            }
        })
        Object.keys(permissions || {}).forEach(role => {
            if (!newPermissions[role] || !permissions[role]) return
            Object.keys(permissions[role]).forEach(scopeType => {
                Object.keys(config.permissionTypes).forEach(permissionType => {
                    if (permissions[role][scopeType] && permissions[role][scopeType][permissionType]) {
                        let validScopes = scopeType === "global" ? globalPermissions : teamPermissions
                        let filteredScopes = permissions[role][scopeType][permissionType].filter(scope => validScopes.includes(scope))
                        if (scopeType === "global" && permissionType !== config.permissionTypes.view && permissionType !== config.permissionTypes.edit) {
                            newPermissions[role].global[config.permissionTypes.remove].push(...filteredScopes)
                            return
                        }
                        if (scopeType === "team") {
                            if (permissionType === config.permissionTypes.view_all || permissionType === config.permissionTypes.edit_all || permissionType === config.permissionTypes.edit) {
                                newPermissions[role].team[permissionType] = filteredScopes
                                return
                            }
                            // Default unrecognized team permission types to view
                            newPermissions[role].team[config.permissionTypes.view].push(...filteredScopes)
                            return
                        }
                        newPermissions[role][scopeType][permissionType] = filteredScopes
                    }
                })
            })
            let assignedGlobalScopes = [
                ...newPermissions[role].global[config.permissionTypes.view],
                ...newPermissions[role].global[config.permissionTypes.edit]
            ]
            let unassignedGlobalScopes = globalPermissions.filter(scope => !assignedGlobalScopes.includes(scope))
            unassignedGlobalScopes.forEach(scope => {
                // news cannot be "invisible" — default it to view
                if (scope === 'news') {
                    newPermissions[role].global[config.permissionTypes.view].push(scope)
                } else {
                    newPermissions[role].global[config.permissionTypes.remove].push(scope)
                }
            })
            let assignedTeamScopes = [
                ...newPermissions[role].team[config.permissionTypes.view],
                ...newPermissions[role].team[config.permissionTypes.edit],
                ...newPermissions[role].team[config.permissionTypes.view_all],
                ...newPermissions[role].team[config.permissionTypes.edit_all]
            ]
            // evaluations, ventr, and warnings default to "invisible" when unassigned
            let unassignedTeamScopes = teamPermissions.filter(scope => !assignedTeamScopes.includes(scope))
            unassignedTeamScopes.forEach(scope => {
                if (scope === 'evaluations' || scope === 'ventr' || scope === 'warnings') {
                    newPermissions[role].team[config.permissionTypes.remove].push(scope)
                } else {
                    newPermissions[role].team[config.permissionTypes.view].push(scope)
                }
            })
        })
        return newPermissions
    }

    let [managersPermissions, setManagersPermissions] = useState({})
    let [savedPermissions, setSavedPermissions] = useState({})
    let [selectedRole, setSelectedRole] = useState(null)

    useEffect(() => {
        if (roles.length > 0 && !selectedRole) {
            setSelectedRole(roles[0])
        }
    }, [roles])

    useEffect(() => {
        if (defaultProfile && roles.includes(defaultProfile)) {
            setSelectedRole(defaultProfile)
        }
    }, [defaultProfile])

    useEffect(() => {
        if (selectedRole) {
            let profile = managersProfiles.find(p => p.name === selectedRole)
            if (profile) {
                dispatch(getManagersPermissions({ profileId: profile.id, profileName: profile.name }))
            }
        }
    }, [selectedRole])

    useEffect(() => {
        if (permissions && roles.length) {
            let built = buildPermissionsFromApi()
            setManagersPermissions(built)
            setSavedPermissions(JSON.parse(JSON.stringify(built)))
        }
    }, [permissions, managersProfiles])

    let hasRoleChanged = (role) => {
        if (!managersPermissions[role] || !savedPermissions[role]) return false
        let current = managersPermissions[role]
        let saved = savedPermissions[role]
        for (let scopeType of ['global', 'team']) {
            if (!current[scopeType] || !saved[scopeType]) return true
            for (let permType of Object.keys(current[scopeType])) {
                let currentArr = [...(current[scopeType][permType] || [])].sort()
                let savedArr = [...(saved[scopeType][permType] || [])].sort()
                if (currentArr.length !== savedArr.length) return true
                for (let i = 0; i < currentArr.length; i++) {
                    if (currentArr[i] !== savedArr[i]) return true
                }
            }
        }
        return false
    }

    let handlePermissionChange = (role, scopeType, scope, newType) => {
        setManagersPermissions(prevState => {
            let updatedPermissions = JSON.parse(JSON.stringify(prevState))
            if (!updatedPermissions[role]) {
                updatedPermissions[role] = {
                    global: { [config.permissionTypes.view]: [], [config.permissionTypes.edit]: [], [config.permissionTypes.remove]: [] },
                    team: { [config.permissionTypes.view]: [], [config.permissionTypes.edit]: [], [config.permissionTypes.view_all]: [], [config.permissionTypes.edit_all]: [] }
                }
            }
            if (!updatedPermissions[role][scopeType]) {
                updatedPermissions[role][scopeType] = scopeType === 'team'
                    ? { [config.permissionTypes.view]: [], [config.permissionTypes.edit]: [], [config.permissionTypes.view_all]: [], [config.permissionTypes.edit_all]: [], [config.permissionTypes.remove]: [] }
                    : { [config.permissionTypes.view]: [], [config.permissionTypes.edit]: [], [config.permissionTypes.remove]: [] }
            }
            Object.keys(config.permissionTypes).forEach(type => {
                let typeKey = config.permissionTypes[type]
                if (!updatedPermissions[role][scopeType][typeKey]) {
                    updatedPermissions[role][scopeType][typeKey] = []
                }
                updatedPermissions[role][scopeType][typeKey] = updatedPermissions[role][scopeType][typeKey].filter(item => item !== scope)
            })
            if (!updatedPermissions[role][scopeType][newType]) {
                updatedPermissions[role][scopeType][newType] = []
            }
            updatedPermissions[role][scopeType][newType].push(scope)
            return updatedPermissions
        })
    }

    let handleUpdateRole = async (role) => {
        let action = await dispatch(updateManagersPermissions({ managers: { [role]: managersPermissions[role] } }))
        if (action.payload?.responseData?.status === 1) {
            setSavedPermissions(prev => ({
                ...prev,
                [role]: JSON.parse(JSON.stringify(managersPermissions[role]))
            }))
        }
    }

    let roleChanged = selectedRole ? hasRoleChanged(selectedRole) : false

    return (
        <div className={pStyles.managersCard}>
            <div className={pStyles.profileDropdown}>
                <Autocomplete
                    value={selectedRole}
                    onChange={(e, newValue) => setSelectedRole(newValue)}
                    options={roles}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={<Langs str="SELECT_PROFILE" />}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px',
                                    fontSize: '0.88rem',
                                    backgroundColor: 'rgba(0, 40, 128, 0.02)',
                                    transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                                    '& fieldset': {
                                        borderColor: 'rgba(0, 40, 128, 0.12)',
                                        transition: 'border-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'rgba(0, 40, 128, 0.25)',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'rgba(255, 144, 21, 0.5)',
                                        borderWidth: '1.5px',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: '0 0 0 3px rgba(255, 144, 21, 0.1)',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontSize: '0.85rem',
                                    color: 'rgba(0, 40, 128, 0.55)',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#FF9015',
                                },
                                // Below 16px iOS Safari zooms the page on focus and
                                // leaves the grid scrolled off-canvas.
                                '& .MuiInputBase-input': {
                                    fontSize: { xs: '16px', sm: '0.88rem' },
                                },
                            }}
                        />
                    )}
                    disableClearable
                    fullWidth
                />
            </div>
            {selectedRole && managersPermissions[selectedRole] && (
                <>
                    <div className={pStyles.permissionsScrollArea}>
                        <RolePermissionCard
                            key={selectedRole}
                            role={selectedRole}
                            rolePermissions={managersPermissions[selectedRole]}
                            globalScopes={globalPermissions}
                            teamScopes={teamPermissions}
                            onPermissionChange={handlePermissionChange}
                        />
                    </div>
                    <div className={pStyles.updateButtonArea}>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={() => handleUpdateRole(selectedRole)}
                            disabled={!roleChanged}
                            className={pStyles.saveBtn}
                        >
                            <Langs str="update" />
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
}
